import { useEffect, useState } from 'react'
import { api } from './api'
import type { NotLive, OperationDoc, ProfileProblem } from './types'

// Basic queue setup, as docs/design/redesign/contracts/basic-config.md defines it.

/** Basic edits services, queues and calling profiles; Advanced is the operation-profile editor. */
export type ConfigMode = 'basic' | 'advanced'

/** At most this many levels in one calling profile. */
export const MAX_LEVELS = 10

export interface BasicQueue {
  id: string
  code: string
  name: Record<string, string>
  letter: string
  firstNumber: number
  lastNumber: number
  targetMin: number
  serviceIds: string[]
}
export type BasicQueueIn = Omit<BasicQueue, 'id' | 'code'>

/** How a level picks among its customers; absent is longest waiting. */
export type LevelOrder = 'max_waiting' | 'closest_to_target'
export const LEVEL_ORDERS: readonly LevelOrder[] = ['max_waiting', 'closest_to_target']

export interface BasicLevel {
  queueIds: string[]
  order?: LevelOrder
}
export interface BasicCallingProfile {
  id: string
  name: string
  levels: BasicLevel[]
}
export type BasicProfileIn = Omit<BasicCallingProfile, 'id'>

export interface BasicService {
  id: string
  code: string
  name: Record<string, string>
  active: boolean
  queueId: string
}

export interface BasicView {
  mode: ConfigMode
  editable: boolean
  problems: ProfileProblem[]
  profile: { id: string; name: string } | null
  revision: number
  services: BasicService[]
  queues: BasicQueue[]
  callingProfiles: BasicCallingProfile[]
  branches: Array<{ id: string; code: string; name: string; pending: boolean; reasons: string[] }>
  id?: string
  warnings: ProfileProblem[]
  notLive?: NotLive
}

export interface BasicWriteAt {
  baseRevision?: number
  organisation?: string
}

/** One operation profile as the Basic screens show it. */
export interface QueueSetup {
  id: string
  name: string
  queues: BasicQueue[]
  callingProfiles: BasicCallingProfile[]
}

/** What Queues and Calling profiles render: editable only through the Basic API. */
export interface QueueScreen {
  /** The mode this read found. */
  mode: ConfigMode | null
  editable: boolean
  revision: number
  setups: QueueSetup[]
  /** Why Basic cannot edit this setup, when the mode is Basic. */
  problems: ProfileProblem[]
  /** Null when the server has no Basic API: services then come from the catalogue. */
  services: BasicService[] | null
}

/** The services routed to each queue: a group's default queue, and any segment's queue. */
export function queueServices(doc: OperationDoc): Map<string, string[]> {
  const by = new Map<string, Set<string>>()
  const add = (queueId: string, ids: readonly string[] | undefined) => {
    if (!queueId || !ids?.length) return
    const set = by.get(queueId) ?? new Set<string>()
    for (const id of ids) set.add(id)
    by.set(queueId, set)
  }
  for (const g of doc.groups ?? []) {
    add(g.defaultQueueId, g.serviceIds)
    for (const s of g.segments ?? []) add(s.queueId, s.serviceIds?.length ? s.serviceIds : g.serviceIds)
  }
  return new Map([...by].map(([k, v]) => [k, [...v]]))
}

/** An operation document read as Basic would show it, for setups Basic cannot express. */
export function setupFromDoc(id: string, name: string, doc: OperationDoc): QueueSetup {
  const services = queueServices(doc)
  const queues = [...(doc.queues ?? [])]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((q) => ({
      id: q.id,
      code: q.code,
      name: q.name,
      letter: q.letter,
      firstNumber: q.firstNumber,
      lastNumber: q.lastNumber,
      targetMin: Math.round(q.serviceLevelSec / 60),
      serviceIds: services.get(q.id) ?? [],
    }))
  const callingProfiles = (doc.workProfiles ?? []).map((w) => {
    const levels = (w.rules ?? [])
      .filter((r) => r.order !== 'do_not_call' && r.queueIds.length > 0)
      .map((r): BasicLevel => ({ queueIds: [...r.queueIds], ...(r.order === 'closest_to_target' ? { order: r.order } : {}) }))
    return { id: w.id, name: w.name, levels: levels.length > 0 ? levels : [{ queueIds: [...w.queueIds] }] }
  })
  return { id, name, queues, callingProfiles }
}

/** Every operation profile of the organisation, read straight from the documents. */
export async function readSetups(organisation?: string): Promise<QueueSetup[]> {
  const { profiles } = await api.operationProfiles(organisation)
  const docs = await Promise.all((profiles ?? []).map((p) => api.operationDocument(p.id)))
  return docs.map((d) => setupFromDoc(d.profile.id, d.profile.name, d.document))
}

/** The Basic view as the screens use it; a Basic view that names no profile reads the documents instead. */
export async function screenFrom(view: BasicView, organisation?: string): Promise<QueueScreen> {
  if (view.profile && view.problems.length === 0) {
    return {
      mode: view.mode,
      editable: view.editable,
      revision: view.revision,
      setups: [{ id: view.profile.id, name: view.profile.name, queues: view.queues ?? [], callingProfiles: view.callingProfiles ?? [] }],
      problems: [],
      services: view.services ?? [],
    }
  }
  return {
    mode: view.mode,
    editable: false,
    revision: view.revision,
    setups: await readSetups(organisation),
    problems: view.mode === 'basic' ? (view.problems ?? []) : [],
    services: view.services ?? null,
  }
}

/** Reads the Basic view. */
export async function readQueueScreen(organisation?: string): Promise<QueueScreen> {
  return screenFrom(await api.basicConfig(organisation), organisation)
}

/** Queue ids already used by another level of the same profile: one queue per level. */
export function takenElsewhere(levels: readonly BasicLevel[], level: number): Set<string> {
  return new Set(levels.flatMap((l, i) => (i === level ? [] : l.queueIds)))
}

/** Why a calling profile cannot be saved yet, or '' when it can. */
export function levelProblem(name: string, levels: readonly BasicLevel[]): 'name' | 'empty' | 'twice' | 'tooMany' | '' {
  if (!name.trim()) return 'name'
  if (levels.length === 0 || levels.length > MAX_LEVELS) return 'tooMany'
  if (levels.some((l) => l.queueIds.length === 0)) return 'empty'
  const all = levels.flatMap((l) => l.queueIds)
  if (new Set(all).size !== all.length) return 'twice'
  return ''
}

/** A refusal's problems with the queue they name (args.queue, a code) given by its name, as Basic shows queues. */
export function problemsNamed(problems: readonly ProfileProblem[], queues: readonly BasicQueue[]): ProfileProblem[] {
  return problems.map((p) => {
    const q = typeof p.args?.queue === 'string' ? queues.find((x) => x.code === p.args!.queue) : undefined
    const name = q && (q.name.en || Object.values(q.name).find(Boolean))
    return name ? { ...p, args: { ...p.args, queue: name } } : p
  })
}

/** Why a queue cannot be saved yet, or '' when it can (the server checks the rest). */
export function queueProblem(q: BasicQueueIn): 'name' | 'letter' | 'range' | 'target' | '' {
  if (!(q.name.en ?? '').trim()) return 'name'
  if (!/^[A-Z]{1,4}$/.test(q.letter)) return 'letter'
  if (q.firstNumber < 1 || q.lastNumber > 9999 || q.lastNumber - q.firstNumber < 9) return 'range'
  if (q.targetMin < 1 || q.targetMin > 240) return 'target'
  return ''
}

/** Why Save is off, said beside it: the numbers or the target. A missing name or letter speaks for itself. */
export function queueHint(q: BasicQueueIn): 'profiles.problem.RANGE_BOUNDS' | 'profiles.problem.RANGE_SHORT' | 'profiles.problem.TARGET_RANGE' | '' {
  const problem = queueProblem(q)
  if (problem === 'range') return q.firstNumber < 1 || q.lastNumber > 9999 ? 'profiles.problem.RANGE_BOUNDS' : 'profiles.problem.RANGE_SHORT'
  if (problem === 'target') return 'profiles.problem.TARGET_RANGE'
  return ''
}

/** A queue's numbers as tickets print them ("001–499"), so queues sharing a letter tell apart. */
export function rangeLabel(q: Pick<BasicQueue, 'firstNumber' | 'lastNumber'>): string {
  const pad = (n: number) => String(n).padStart(3, '0')
  return `${pad(q.firstNumber)}–${pad(q.lastNumber)}`
}

/** The queue another queue's services already go to, by service id. */
export function servicesElsewhere(queues: readonly BasicQueue[], queueId: string): Map<string, BasicQueue> {
  const by = new Map<string, BasicQueue>()
  for (const q of queues) if (q.id !== queueId) for (const s of q.serviceIds) by.set(s, q)
  return by
}

/**
 * The organisation's configuration mode. Null until known, and on a server without
 * the Basic API, where Advanced stays the only editor. A new `generation` reads it again.
 */
export function useConfigMode(organisation: string | undefined, enabled: boolean, generation = 0): ConfigMode | null {
  const [mode, setMode] = useState<ConfigMode | null>(null)
  useEffect(() => setMode(null), [organisation, enabled])
  useEffect(() => {
    if (!enabled) return
    let live = true
    // The Basic view carries the mode; Options (admin/Options.tsx) owns /config/mode.
    api
      .basicConfig(organisation)
      .then((v) => live && setMode(v.mode))
      .catch(() => live && setMode(null))
    return () => {
      live = false
    }
  }, [organisation, enabled, generation])
  return mode
}
